import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    Alert,
    Box,
    Chip,
    Checkbox,
} from '@mui/material';
import { Email, Warning, Person, Send } from '@mui/icons-material';
import { ParticipantData } from '../../../interface/Participant.interface';
import '../../../styles/PaymentReminderModal.css';

interface PaymentReminderModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (selectedParticipants: ParticipantData[], customMessage?: string) => void;
    participants: ParticipantData[];
    isLoading: boolean;
    errorMessage?: string | null;
    successMessage?: string | null;
}

export const PaymentReminderModal: React.FC<PaymentReminderModalProps> = ({
                                                                              open,
                                                                              onClose,
                                                                              onConfirm,
                                                                              participants,
                                                                              isLoading,
                                                                              errorMessage,
                                                                              successMessage,
                                                                          }) => {
    const [customMessage, setCustomMessage] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Initialise tous cochés à l’ouverture
    useEffect(() => {
        if (open) {
            setSelectedIds(new Set(participants.map((p) => p.uuid)));
            setCustomMessage('');
        }
    }, [open, participants]);

    const handleConfirm = () => {
        const selectedParticipants = participants.filter((p) => selectedIds.has(p.uuid));
        onConfirm(selectedParticipants, customMessage.trim() || undefined);
    };

    const handleToggleParticipant = (id: string) => {
        setSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleClose = () => {
        if (!isLoading) {
            setCustomMessage('');
            onClose();
        }
    };

    const selectedCount = selectedIds.size;
    const totalCount = participants.length;

    const handleToggleAll = () => {
        if (selectedIds.size === totalCount) {
            setSelectedIds(new Set()); // tout décocher
        } else {
            setSelectedIds(new Set(participants.map((p) => p.uuid))); // tout cocher
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            className="payment-reminder-dialog"
            PaperProps={{
                className: 'payment-reminder-paper',
            }}
            aria-labelledby="payment-reminder-title"
        >
            <DialogTitle id="payment-reminder-title" className="payment-reminder-title">
                <Box className="title-container">
                    <div className="title-icon">
                        <Email />
                    </div>
                    <Typography variant="h5" className="title-text">
                        Envoyer des relances de paiement
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent className="payment-reminder-content">
                {/* Affichage des retours (erreur / succès) */}
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMessage}
                    </Alert>
                )}

                {totalCount === 0 ? (
                    <Alert severity="info" className="reminder-alert">
                        Aucun participant n’est en retard de paiement pour ce mois.
                    </Alert>
                ) : (
                    <Alert severity="info" className="reminder-alert">
                        <Box className="alert-content">
                            <Warning className="alert-icon" />
                            <Typography variant="body2" className="alert-text">
                                Vous vous apprêtez à envoyer un email de relance à{' '}
                                <strong>{selectedCount}</strong> participant
                                {selectedCount > 1 ? 's' : ''} (sur {totalCount} en retard).
                            </Typography>
                        </Box>
                    </Alert>
                )}

                {totalCount > 0 && (
                    <>


                        <div className="participants-section">
                            <Typography variant="h6" className="section-subtitle">
                                Participants concernés :
                                <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                                    <Checkbox
                                        indeterminate={selectedCount > 0 && selectedCount < totalCount}
                                        checked={selectedCount === totalCount}
                                        onChange={handleToggleAll}
                                        disabled={isLoading}
                                    />
                                    <Typography variant="body2">
                                        {selectedCount === totalCount
                                            ? 'Tout décocher'
                                            : 'Tout cocher'}
                                    </Typography>
                                </Box>
                            </Typography>

                            <List className="participants-list" dense>
                                {participants.map((participant) => {
                                    const fullName = `${participant.firstname ?? ''} ${participant.lastname ?? ''}`.trim() || 'Inconnu';
                                    const checked = selectedIds.has(participant.uuid);

                                    return (
                                        <ListItem
                                            key={participant.uuid}
                                            className="participant-item"
                                            secondaryAction={
                                                <Checkbox
                                                    edge="end"
                                                    checked={checked}
                                                    onChange={() => handleToggleParticipant(participant.uuid)}
                                                    disabled={isLoading}
                                                />
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar className="participant-avatar">
                                                    <Person />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={fullName}
                                                secondary={
                                                    <Box className="participant-status">
                                                        <Chip
                                                            label="En retard"
                                                            size="small"
                                                            color="error"
                                                            className="late-chip"
                                                        />
                                                    </Box>
                                                }
                                                className="participant-text"
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </div>
                    </>
                )}

                <div className="message-section">
                    <TextField
                        label="Message personnalisé (optionnel)"
                        multiline
                        rows={3}
                        fullWidth
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder="Ajoutez un message personnalisé qui sera inclus dans l'email de relance..."
                        className="custom-message-field"
                        inputProps={{ maxLength: 500 }}
                        helperText={`${customMessage.length}/500 caractères`}
                    />

                    <Typography variant="caption" className="info-text">
                        Un email automatique sera envoyé avec les détails du projet et le montant dû.
                    </Typography>
                </div>
            </DialogContent>

            <DialogActions className="payment-reminder-actions">
                <Button
                    onClick={handleClose}
                    disabled={isLoading}
                    className="cancel-button"
                >
                    Annuler
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={isLoading || selectedCount === 0}
                    startIcon={isLoading ? undefined : <Send />}
                    className="send-button"
                >
                    {isLoading ? 'Envoi...' : 'Envoyer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
